import { type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { type UserOrderResponseWithFlags } from '@redteclab/api/clients/bully'

import { Button } from 'base-ui'

import { Experiment } from '../../experiments/components/Experiment'
import { ExperimentVariant } from '../../experiments/components/ExperimentVariant'
import { EXPERIMENT_NAME_DIRECT_CONTACT_FORM_DIALOG } from '../../experiments/model/EXPERIMENT_NAME'
import { EXPERIMENT_VARIATION } from '../../experiments/model/EXPERIMENT_VARIATION'
import { useGlobalConfigContext } from '../../global-config'
import { OrderEmptyOrdersBox } from '../../order'
import { TENANT } from '../../tenant'

import { ContactPageCtaList } from './ContactPageCtaList'
import { ContactPageFlatCta } from './ContactPageFlatCta'
import { ContactPageOrderCardList } from './ContactPageOrderCardList'

const MAX_ORDERS = 3

export const ContactPageOrdersSectionContent: FC<{
  handleAccountManagementModalOpen: () => void
  handleLoginModalOpen: () => void
  isUserLoggedIn: boolean
  orders?: UserOrderResponseWithFlags[]
  ordersSectionData: {
    ctaId: string
    ctaLabel: string
    description: string
    href?: string
  }
}> = ({
  handleAccountManagementModalOpen,
  handleLoginModalOpen,
  isUserLoggedIn,
  orders,
  ordersSectionData,
}) => {
  const config = useGlobalConfigContext()
  const { tenant } = config

  return (
    <div className="w-full tablet:container">
      <Experiment names={[EXPERIMENT_NAME_DIRECT_CONTACT_FORM_DIALOG]}>
        <ExperimentVariant name={EXPERIMENT_VARIATION.DEFAULT}>
          {orders && orders.length > 0 ? (
            <ContactPageOrderCardList maxOrders={MAX_ORDERS} orders={orders} />
          ) : null}
          {tenant !== TENANT.COM && isUserLoggedIn ? (
            <Button<'a'>
              className="mt-4 w-full tablet:mt-12 tablet:w-auto"
              color="secondary"
              data-qa-id={`qa-id-${ordersSectionData.ctaId}`}
              href={ordersSectionData.href}
              id={ordersSectionData.ctaId}
              slots={{ root: 'a' }}
              variant="filled"
            >
              {ordersSectionData.ctaLabel}
            </Button>
          ) : null}
          {tenant === TENANT.COM && isUserLoggedIn ? (
            <div className="mt-4 flex flex-col gap-4 tablet:mt-12 tablet:flex-row tablet:justify-between tablet:gap-6">
              {(orders?.length && orders.length > MAX_ORDERS) ||
              orders?.length === 0 ? (
                <ContactPageFlatCta<'a'>
                  data-qa-id={`qa-id-${ordersSectionData.ctaId}`}
                  href={ordersSectionData.href}
                  id={ordersSectionData.ctaId}
                  slots={{ root: 'a' }}
                >
                  {ordersSectionData.ctaLabel}
                </ContactPageFlatCta>
              ) : null}

              <ContactPageFlatCta
                data-qa-id="qa-id-help-center-account-management"
                onClick={handleAccountManagementModalOpen}
              >
                <FormattedMessage id="helpCenter.accountManagement.dialog.title.new" />
              </ContactPageFlatCta>
            </div>
          ) : null}
          {!isUserLoggedIn && (
            <div className="mt-4 flex flex-col gap-4 tablet:mt-12 tablet:flex-row tablet:justify-center tablet:gap-6">
              <Button
                className="mt-4 w-full tablet:mt-0 tablet:w-auto"
                color="secondary"
                data-qa-id={`qa-id-${ordersSectionData.ctaId}`}
                id={ordersSectionData.ctaId}
                onClick={handleLoginModalOpen}
                variant="filled"
              >
                {ordersSectionData.ctaLabel}
              </Button>
            </div>
          )}
        </ExperimentVariant>
        <ExperimentVariant name={EXPERIMENT_VARIATION.V1}>
          <>
            {orders && orders.length > 0 ? (
              <ContactPageOrderCardList
                maxOrders={MAX_ORDERS}
                orders={orders}
              />
            ) : null}
            {isUserLoggedIn && (!orders || orders.length === 0) ? (
              <OrderEmptyOrdersBox />
            ) : null}
            <ContactPageCtaList
              handleLoginModalOpen={handleLoginModalOpen}
              isUserLoggedIn={isUserLoggedIn}
              maxOrders={MAX_ORDERS}
              orders={orders}
            />
          </>
        </ExperimentVariant>
      </Experiment>
    </div>
  )
}
